import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import useAppStore from 'store/useAppStore';

// 1029：API 位址統一在 src/apiConfig.js（'/api'，跟網頁同一個網域）
import { API_BASE_URL } from 'apiConfig';
export { API_BASE_URL };

const api = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: [
    'getSegScriptList',
    'getFullScriptList',
    'getTempProjectList',
    'getProjectList',
  ],
  endpoints: (build) => ({
    accountRegister: build.mutation({
      query: ({ user_id, user_password, user_name, comp_name, industry }) => ({
        url: '/accountRegister/',
        method: 'POST',
        body: { user_id, user_password, user_name, comp_name, industry },
      }),
      transformResponse: (response) => {
        if (response.status !== '帳號註冊成功') {
          throw new Error(response.status);
        }
        return response;
      },
    }),
    userLogin: build.mutation({
      query: ({ user_id, user_password }) => ({
        url: '/userLogin/',
        method: 'POST',
        body: { user_id, user_password },
      }),
      transformResponse: (response) => {
        if (response.status !== '登入成功') {
          throw new Error(response.status);
        }
        return response;
      },
    }),
    getProjectList: build.query({
      query: ({ user_id, comp_name }) => ({
        url: '/getProjectList/',
        method: 'POST',
        body: { user_id, comp_name },
      }),
      providesTags: ['getProjectList'],
    }),


    getTempProjectList: build.query({
      query: ({ user_id, comp_name }) => ({
        url: '/getTempProjectList/',
        method: 'POST',
        body: { user_id, comp_name },
      }),
      providesTags: ['getTempProjectList'],
    }),
    projectTempSave: build.mutation({
      query: (formData) => ({
        url: '/projectTempSave/',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response) => {
        if (response.status !== '專案暫存成功') {
          throw new Error(response.status);
        }
        return response;
      },
      invalidatesTags: ['getTempProjectList'],
    }),
    projectCreation: build.mutation({
      query: (formData) => ({
        url: '/projectCreation/',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response) => {
        if (response.status !== '專案建立成功') {
          throw new Error(response.status);
        }
        return response;
      },
      invalidatesTags: ['getProjectList'],
    }),
    getRelatedProjectList: build.query({
      query: ({
        user_id,
        comp_name,
        project_id,
        hand_transp = [],
        station = [],
        seg_script = [],
      }) => ({
        url: '/getRelatedProjectList/',
        method: 'POST',
        body: {
          user_id,
          comp_name,
          project_id,
          hand_transp: JSON.stringify(hand_transp),
          station: JSON.stringify(station),
          seg_script: JSON.stringify(seg_script),
        },
      }),
    }),
    extractKeyInfo: build.mutation({
      query: (formData) => {
        return {
          url: '/extractKeyInfo/',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getKeyInfo: build.mutation({
      query: ({ user_id, comp_name, ori_project_id }) => ({
        url: '/getKeyInfo/',
        method: 'POST',
        body: { user_id, comp_name, ori_project_id },
      }),
    }),
    getTempKeyInfo: build.mutation({
      query: ({ user_id, comp_name, project_id, edit_time }) => ({
        url: '/getTempKeyInfo/',
        method: 'POST',
        body: { user_id, comp_name, project_id, edit_time },
      }),
    }),
    getFinalKeyInfo: build.mutation({
      query: ({ user_id, comp_name, project_id }) => ({
        url: '/getFinalKeyInfo/',
        method: 'POST',
        body: { user_id, comp_name, project_id },
      }),
    }),
    // 取得區段流程列表
    getSegScriptList: build.query({
      query: ({ seg_script_id, flag, user_id, comp_name, project_id }) => ({
        url: '/getSegScriptList/',
        method: 'POST',
        body: { seg_script_id, flag, user_id, comp_name, project_id },
      }),
      providesTags: ['getSegScriptList'],
    }),
    genSegScript: build.query({
      query: ({
        hand_transp,
        station,
        seg_script,
        related_project_id,
        ori_project_id,
        project_id,
        user_id,
        comp_name,
      }) => ({
        url: '/genSegScript/',
        method: 'POST',
        body: {
          hand_transp,
          station,
          seg_script,
          related_project_id,
          ori_project_id,
          project_id,
          user_id,
          comp_name,
        },
      }),
    }),
    save2DBFullScript: build.mutation({
      query: ({
        hand_transp,
        station,
        all_seg_script,
        full_script_id,
        project_id,
        user_id,
        comp_name,
        edit_time,
        full_script,
      }) => ({
        url: '/save2DBFullScript/',
        method: 'POST',
        body: {
          hand_transp: JSON.stringify(hand_transp),
          station: JSON.stringify(station),
          all_seg_script: JSON.stringify(all_seg_script),
          full_script_id,
          project_id,
          user_id,
          comp_name,
          edit_time,
          full_script,
        },
      }),
      transformResponse: (response) => {
        if (response.status !== '全局腳本儲存成功') {
          throw new Error(response.status);
        }
        return response;
      },
      invalidatesTags: ['getFullScriptList'],
    }),
    deleteDBSegScript: build.mutation({
      query: ({
        seg_script_name,
        project_id,
        seg_script_id,
        user_id,
        comp_name,
        edit_time,
      }) => ({
        url: '/deleteDBSegScript/',
        method: 'POST',
        body: {
          seg_script_name,
          project_id,
          seg_script_id,
          user_id,
          comp_name,
          edit_time,
        },
      }),
      invalidatesTags: ['getSegScriptList'],
    }),
    segScriptChat: build.mutation({
      query: ({
        session_id,
        project_id,
        seg_script_id,
        seg_script,
        question,
      }) => ({
        url: '/segScriptChat/',
        method: 'POST',
        body: {
          session_id,
          project_id,
          seg_script_id,
          seg_script,
          question,
        },
      }),
    }),
    save2DBSegScript: build.mutation({
      query: ({
        hand_transp,
        station,
        seg_script_data,
        seg_script_name,
        project_id,
        seg_script_id,
        user_id,
        comp_name,
        edit_time,
        seg_script,
      }) => ({
        url: '/save2DBSegScript/',
        method: 'POST',
        body: {
          hand_transp,
          station,
          seg_script_data,
          seg_script_name,
          project_id,
          seg_script_id,
          user_id,
          comp_name,
          edit_time,
          seg_script,
        },
      }),
      transformResponse: (response) => {
        if (response.status !== '區段腳本儲存成功') {
          throw new Error(response.status);
        }
        return response;
      },
      invalidatesTags: ['getSegScriptList'],
    }),
    genFullScript: build.query({
      query: ({
        hand_transp,
        station,
        all_seg_script,
        full_script_id,
        related_project_id,
        ori_project_id,
        project_id,
        user_id,
        comp_name,
      }) => ({
        url: '/genFullScript/',
        method: 'POST',
        body: {
          hand_transp: JSON.stringify(hand_transp),
          station: JSON.stringify(station),
          all_seg_script: JSON.stringify(
            all_seg_script.map(({ id, ...others }) => {
              return {
                seg_script_id: id,
                seg_script_info: others,
              };
            }),
          ),
          full_script_id,
          related_project_id: JSON.stringify(related_project_id),
          ori_project_id,
          project_id,
          user_id,
          comp_name,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          useAppStore.setState({
            editedFullScript: data.full_script,
          });

          // eslint-disable-next-line no-empty
        } catch { }
      },
    }),
    getFullScriptList: build.query({
      query: ({ full_script_id, flag, user_id, comp_name, project_id }) => ({
        url: '/getFullScriptList/',
        method: 'POST',
        body: { full_script_id, flag, user_id, comp_name, project_id },
      }),
      providesTags: ['getFullScriptList'],
    }),
    deleteDBFullScript: build.mutation({
      query: ({
        full_script_id,
        project_id,
        user_id,
        comp_name,
        edit_time,
      }) => ({
        url: '/deleteDBFullScript/',
        method: 'POST',
        body: { full_script_id, project_id, user_id, comp_name, edit_time },
      }),
      invalidatesTags: ['getFullScriptList'],
    }),
    fullScriptChat: build.mutation({
      query: ({
        session_id,
        project_id,
        full_script_id,
        full_script,
        question,
      }) => ({
        url: '/fullScriptChat/',
        method: 'POST',
        body: { session_id, project_id, full_script_id, full_script, question },
      }),
    }),

    // 1029 新增：更換專案的報價單 / 配置圖
    replaceProjectFile: build.mutation({
      query: (formData) => ({
        url: '/replaceProjectFile/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['getTempProjectList'],
    }),

    uploadSpecInput: build.mutation({
      query: (formData) => ({
        url: '/uploadSpecInput/',
        method: 'POST',
        body: formData,
      }),
    }),

    // 0529 新增：下載專案設計規格書 Word 檔 (.docx)
    downloadDesignSpecWord: build.query({
      query: ({
        user_id,
        comp_name,
        project_id,
      }) => ({
        // RTK Query 偵測到 url 是 https:// 開頭時，會自動忽略預設的 baseUrl，改走此絕對路徑
        url: `/getSpecDocx/`,
        method: 'POST',
        body: { user_id, comp_name, project_id },
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

  }),
});
// 0529 新增
// 🎯 關鍵：你必須在這裡明確地把這個自動生成的 Hook export 出來！
// export const {
//   useLazyDownloadDesignSpecWordQuery
// } = api;
export const {
  useLazyDownloadDesignSpecWordQuery,
  useUploadSpecInputMutation,
  useReplaceProjectFileMutation,
} = api;
export default api;
